import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { policyApi } from "./usePolicyApi";
import { POLICY_ERROR_MESSAGES } from "../constants";
import queryKeys from "../../../lib/query/queryKeys";

export const usePolicyManagement = () => {
  const queryClient = useQueryClient();
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const {
    data: policies = [],
    isLoading: loading,
    error: queryError,
    refetch: fetchPolicies,
  } = useQuery({
    queryKey: queryKeys.policies.list(),
    queryFn: () => policyApi.getAllPolicies(),
    staleTime: 2 * 60 * 1000,
  });

  const error = queryError
    ? queryError.response?.data?.["error-message"] || POLICY_ERROR_MESSAGES.FETCH_POLICIES
    : null;

  const createMutation = useMutation({
    mutationFn: policyData => policyApi.createPolicy(policyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ policyId, policyData }) => policyApi.updatePolicy(policyId, policyData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: policyId => policyApi.deletePolicy(policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
    },
  });

  const createPolicy = useCallback(
    async policyData => {
      return createMutation.mutateAsync(policyData);
    },
    [createMutation]
  );

  const updatePolicy = useCallback(
    async (policyId, policyData) => {
      return updateMutation.mutateAsync({ policyId, policyData });
    },
    [updateMutation]
  );

  const deletePolicy = useCallback(
    async policyId => {
      return deleteMutation.mutateAsync(policyId);
    },
    [deleteMutation]
  );

  const selectPolicy = useCallback(policy => {
    setSelectedPolicy(policy);
  }, []);

  const clearError = useCallback(() => {
    // React Query manages error state automatically
  }, []);

  const resetState = useCallback(() => {
    setSelectedPolicy(null);
    queryClient.removeQueries({ queryKey: queryKeys.policies.all });
  }, [queryClient]);

  return {
    policies,
    loading: loading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    error,
    selectedPolicy,
    fetchPolicies,
    createPolicy,
    updatePolicy,
    deletePolicy,
    selectPolicy,
    clearError,
    resetState,
  };
};
